import { selectAssetSummaryArticles, selectTitleDetail } from '@repo/api-adapters/selectors/cart.selectors';
import {
    selectAssetCreationDate,
    selectCustomerMeetingPointName,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';
import type { AssetArticle, AssetDataType } from '@repo/types/asset';
import { formatDate } from '@repo/utils';
import type { PickingListPreset } from '@sp-saljapro/pdf-generator';

import { getArticleNo } from '@/utils/article.utils';
import { formatNumber, formatPrice } from '@/utils/translation.utils';
import { getAppConfig } from '@/utils/window.utils';

const formatPickingLocation = (item: AssetArticle): string | undefined => {
    const selfServeLocation =
        item.selfServeSalesLocations?.Aisle && item.selfServeSalesLocations.Bin
            ? translate('pdf_picking_location_self_serve', {
                  aisle: item.selfServeSalesLocations.Aisle,
                  bin: item.selfServeSalesLocations.Bin,
              })
            : undefined;

    return selfServeLocation ?? item.serveSalesLocations?.Department;
};

const formatMeasurement = (value: number | undefined, unit: string | undefined): string | undefined => {
    const formattedValue = formatNumber(value);
    return formattedValue && unit ? `${formattedValue} ${unit}` : undefined;
};

/**
 * Sums the values that are present. Articles missing a measurement are skipped rather
 * than blanking the whole total; `undefined` is only returned when nothing is present,
 * which renders the row with an empty value.
 */
const sumValues = (values: Array<number | undefined>): number | undefined => {
    const definedValues = values.filter((value): value is number => value !== undefined);
    return definedValues.length > 0 ? definedValues.reduce((total, value) => total + value, 0) : undefined;
};

const calculateArticleTotal = (value: number | undefined, quantity: number | undefined): number | undefined =>
    value === undefined || quantity === undefined ? undefined : value * quantity;

export const buildPickingListPdfData = (
    data: AssetDataType | undefined,
    assetId: string
): PickingListPreset | undefined => {
    const { measurementSystem, dateFormat } = getAppConfig();
    const selfServeArticles = selectAssetSummaryArticles(data, { measurementSystem }).filter(
        (item) => item.isSelfServe === true
    );
    const showPriceExcludingTax = Boolean(getAppConfig().feature.priceExclTax?.enable);
    const titleDetail = selectTitleDetail(data);
    const currency = titleDetail?.currency;
    const weightUnit = selfServeArticles.find((item) => item.packageWeight)?.packageWeight?.unit;
    const volumeUnit = selfServeArticles.find((item) => item.packageVolume)?.packageVolume?.unit;
    const pickingListItemsWithTotals = selfServeArticles.map((item) => {
        const unitPrice = showPriceExcludingTax ? item.unitPriceExcludingTax : item.unitPriceIncludingTax;
        const itemTotalPrice = showPriceExcludingTax ? item.totalPriceExcludingTax : item.totalPriceIncludingTax;
        const totalPrice = itemTotalPrice ?? calculateArticleTotal(unitPrice, item.quantity);
        const totalWeight = calculateArticleTotal(item.packageWeight?.value, item.quantity);
        const unitWeight = formatNumber(item.packageWeight?.value);
        const formattedTotalWeight = formatNumber(totalWeight);

        return {
            item: {
                quantity: formatNumber(item.quantity),
                description: [item.name, item.description].filter(Boolean).join(' '),
                articleNumber: item.itemId ? getArticleNo(item.itemId) : undefined,
                pickingLocation: formatPickingLocation(item),
                weight: unitWeight && formattedTotalWeight ? `${unitWeight} / ${formattedTotalWeight}` : undefined,
                price: formatPrice(unitPrice, currency, { includeCurrency: false }),
                totalPrice: formatPrice(totalPrice, currency, { includeCurrency: false }),
            },
            totalPrice,
        };
    });

    if (pickingListItemsWithTotals.length === 0) {
        return;
    }

    const assetCreationDate = selectAssetCreationDate(data);
    const pickingListTotalPrice = sumValues(pickingListItemsWithTotals.map(({ totalPrice }) => totalPrice));
    const packageCount = sumValues(
        selfServeArticles.map((item) => calculateArticleTotal(item.numberOfPackages, item.quantity))
    );
    const weight = sumValues(
        selfServeArticles.map((item) => calculateArticleTotal(item.packageWeight?.value, item.quantity))
    );
    const volume = sumValues(
        selfServeArticles.map((item) => calculateArticleTotal(item.packageVolume?.value, item.quantity))
    );

    return {
        title: translate('pdf_picking_list_title'),
        orderNumber: assetId,
        orderNumberLabel: translate('pdf_order_number'),
        pickingList: {
            title: translate('pdf_product_overview'),
            headers: {
                quantity: translate('pdf_quantity'),
                description: translate('pdf_description'),
                articleNumber: translate('pdf_article_number'),
                pickingLocation: translate('pdf_picking_location'),
                weight: weightUnit ? translate('pdf_weight_unit_total', { unit: weightUnit }) : translate('pdf_weight'),
                price: currency ? translate('pdf_price_currency', { currency }) : translate('pdf_price'),
                totalPrice: translate('pdf_total_price'),
            },
            items: pickingListItemsWithTotals.map(({ item }) => item),
        },
        noteList: {
            title: translate('pdf_notes_title'),
            items: [translate('pdf_picking_note_1'), translate('pdf_picking_note_2'), translate('pdf_picking_note_3')],
        },
        footer: {
            storeLabel: translate('pdf_store'),
            store: selectCustomerMeetingPointName(data),
            createdDateLabel: translate('pdf_created'),
            createdDate: assetCreationDate ? formatDate(assetCreationDate, dateFormat) : undefined,
            orderNumberLabel: translate('pdf_order_number'),
            orderNumber: assetId,
            printoutDateLabel: translate('pdf_printout_date'),
            printoutDate: formatDate(new Date().toISOString(), dateFormat),
            pageLabel: translate('pdf_page'),
        },
        keyValues: {
            rows: [
                { label: translate('pdf_package_count'), value: formatNumber(packageCount) },
                { label: translate('pdf_weight'), value: formatMeasurement(weight, weightUnit) },
                { label: translate('pdf_volume'), value: formatMeasurement(volume, volumeUnit) },
            ],
        },
        amountSummary: {
            title: translate('pdf_payment_summary'),
            currencyNote: currency ? translate('pdf_currency_note', { currency }) : undefined,
            descriptionHeader: translate('pdf_description'),
            amountHeader: translate('pdf_amount'),
            rows: [
                {
                    description: translate('asset_price_summary_items'),
                    amount: formatPrice(pickingListTotalPrice, currency, { includeCurrency: false }),
                },
                {
                    description: translate('pdf_total_price'),
                    amount: formatPrice(pickingListTotalPrice, currency, { includeCurrency: false }),
                    highlighted: true,
                },
            ],
        },
    };
};
